import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useHelpscoutMetadataLazyQuery } from '../models/graphql';
import { BeaconArgs, BeaconType } from '../models/Beacon';
import { useAuth } from '../hooks/useAuth';
import scssVars from '../assets/scss/exportedVars.module.scss';
import { useUserData } from '../hooks/useUserData';
import { useSelectedOrganization } from '../hooks/useSelectedOrganization';

// Extend the Window object to support Beacon.
declare global {
  interface Window {
    Beacon?: BeaconType;
  }
}

// This does not get created here
// It is currently in Account.apollo.tsx
// gql`
//   query HelpscoutMetadata {
//     helpscoutMetadata {
//       beaconId
//       name
//       email
//       userId
//       signature
//     }
//   }
// `;

const BEACON_CLOSE_BUTTON_ID = 'beacon_close_button';
const BEACON_CONTAINER_CLASS = '.BeaconContainer';
const BEACON_SCRIPT_SRC = 'https://beacon-v2.helpscout.net';

const addCloseButtonToBeacon = (): void => {
  // Exit if close button already exists
  if (document.getElementById(BEACON_CLOSE_BUTTON_ID)) return;

  const closeButton = createOrCloneButton();

  // Style and configure close button
  configureCloseButton(closeButton);

  // Append the close button to the beacon container
  const beaconContainer = document.querySelector(BEACON_CONTAINER_CLASS);
  if (beaconContainer) {
    beaconContainer.appendChild(closeButton);
    beaconContainer.classList.add('has_close_button');
  }
};

const createOrCloneButton = (): HTMLElement => {
  const originalButton = document.getElementById(BEACON_CLOSE_BUTTON_ID);
  return originalButton ? (originalButton.cloneNode(true) as HTMLElement) : document.createElement('button');
};

const configureCloseButton = (button: HTMLElement): void => {
  Object.assign(button.style, {
    position: 'absolute',
    top: '-30px',
    right: '0',
    border: `1px solid ${scssVars['green']}`,
    borderRadius: '50%',
    width: '25px',
    height: '25px',
    fontWeight: '100',
    backgroundColor: scssVars['green'],
    color: scssVars['white'],
    cursor: 'pointer',
    fontFamily: scssVars['body-font-family'],
    fontSize: '18px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  });

  button.id = BEACON_CLOSE_BUTTON_ID;
  button.innerText = 'X';
  button.classList.remove('hidden');

  // Add click event to close button
  button.addEventListener('click', () => {
    const beaconContainer = document.querySelector(BEACON_CONTAINER_CLASS);
    if (!beaconContainer) return;

    const clonedButton = beaconContainer.querySelector(`#${BEACON_CLOSE_BUTTON_ID}_clone`);
    if (clonedButton) {
      beaconContainer.removeChild(clonedButton);
    }

    if (typeof window.Beacon === 'function') {
      window.Beacon('close');
    }
  });
};

// This function injects the Beacon script into the document head.
// It either does this immediately if the document has fully loaded, or waits for the window's load event.
const injectBeaconScript = () => {
  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.async = true;
  scriptElement.src = BEACON_SCRIPT_SRC;
  document.head.appendChild(scriptElement);
};

// Static configuration for Beacon labels.
const beaconConfig = {
  messaging: {
    contactForm: {
      showSubject: true,
      showName: true
    }
  },
  labels: {
    weUsuallyRespondIn: 'We usually respond within',
    aFewHours: '24 hours',
    messageConfirmationText: 'You should receive an email reply within 24 hours.',
    searchLabel: 'Search our Help articles...'
  },
  color: scssVars['blue'],
  showPrefilledCustomFields: true
};

// Wraps Beacon calls to ensure they're safe even if Beacon wasn't loaded.
const createSafeBeacon = (isBeaconScriptLoaded: boolean): BeaconType => {
  const beaconFunction = ((...args: BeaconArgs): void => {
    if (isBeaconScriptLoaded && window.Beacon) {
      window.Beacon(...args);
    }
  }) as BeaconType;

  beaconFunction.readyQueue = [];

  return beaconFunction;
};

const DuplicateScriptError = (message: string): Error => {
  const error: Error & { name?: string } = new Error(message);
  error.name = 'DuplicateScriptError';
  return error;
};

// This moves the Fab offscreen
const addStylesToHeader = () => {
  const style = document.createElement('style');
  style.innerHTML = `
    ${BEACON_CONTAINER_CLASS} {
      width: 85vw !important;
      max-width: 700px !important;
      z-index: ${scssVars['z-index-beacon']} !important;
      bottom: 40px !important;
      margin-top: 105px !important;
      min-height: 150px !important;
      max-height: calc(100vh - 60px - 54px - 40px - 60px) !important;
    }
    .BeaconFabButtonFrame {
      display: none;
      right: 9999px;
    }
    #BeaconInlineArticlesFrame {
      z-index: ${scssVars['z-index-beacon']} !important;
    }
    @media (max-width: ${scssVars['breakpoint-medium']}) {
      ${BEACON_CONTAINER_CLASS} {
          position: absolute !important;
          width: 90vw !important;
          max-height: calc(95vh - 120px) !important;
      }
      .hsds-beacon .is-mobile iframe {
        border-top-left-radius: unset !important;
        border-top-right-radius: unset !important;
      }
    }
    button[data-cy="beacon-close-button"] {
      display: none !important;
    }

    @media (max-width: ${scssVars['xxlarge']}) {
      ${BEACON_CONTAINER_CLASS}.is-mobile {
        top: 60px !important;
        transform: translateX(5%) !important;
      }
      ${BEACON_CONTAINER_CLASS}.is-mobile:before {
        transform: translateX(-5%) !important;
      }
    }
  `;

  document.head.appendChild(style);
};

// Asynchronously injects the Beacon script into the DOM.
const loadBeaconScriptToDom = async () => {
  // Throw error if the script is already in the DOM
  if (document.querySelector(`script[src="${BEACON_SCRIPT_SRC}"]`)) {
    throw DuplicateScriptError('Script is already loaded');
  }

  const beaconFunction: BeaconType = (...args: BeaconArgs) => {
    const t = args[0];
    const n = args[1];
    const a = args[2];

    beaconFunction.readyQueue.push({ method: t, options: n, data: a });
  };

  beaconFunction.readyQueue = [];
  window.Beacon = beaconFunction;

  // Inject the Beacon script either immediately if the document is fully loaded, or on window load.
  if ('complete' === document.readyState) {
    injectBeaconScript();
  } else {
    window.addEventListener('load', injectBeaconScript, false);
  }

  addStylesToHeader();
};

export const BeaconContext = createContext<{ beacon: BeaconType }>({ beacon: createSafeBeacon(false) });

export const BeaconProvider = ({ children }: { children: React.ReactNode }) => {
  const { isLoggedIn } = useAuth();
  const { userData } = useUserData();
  const { selectedOrganization, selectedWorkspace } = useSelectedOrganization();
  const [isBeaconScriptLoaded, setIsBeaconScriptLoaded] = useState(false);
  const [getHelpscoutMetadataQuery, helpscoutMetadataResult] = useHelpscoutMetadataLazyQuery({ variables: {} });
  const safeBeacon = useMemo(() => createSafeBeacon(isBeaconScriptLoaded), [isBeaconScriptLoaded]);

  useEffect(() => {
    if (
      isBeaconScriptLoaded &&
      helpscoutMetadataResult.data?.helpscoutMetadata &&
      isLoggedIn &&
      userData &&
      selectedOrganization &&
      window.Beacon
    ) {
      window.Beacon('identify', {
        name: userData.firstName,
        email: helpscoutMetadataResult.data.helpscoutMetadata.email,
        u_id: userData.id,
        org_id: selectedOrganization.id,
        ws_id: selectedWorkspace?.id,
        m_id: selectedOrganization.legacyMemberId,
        signature: helpscoutMetadataResult.data.helpscoutMetadata.signature
      });
    }
  }, [helpscoutMetadataResult, isBeaconScriptLoaded, isLoggedIn, selectedOrganization, selectedWorkspace, userData]);

  useEffect(() => {
    if (isLoggedIn) {
      getHelpscoutMetadataQuery().then(result => {
        const helpscoutMetaData = result.data?.helpscoutMetadata;
        if (helpscoutMetaData && !isBeaconScriptLoaded) {
          loadBeaconScriptToDom()
            .then(() => {
              setIsBeaconScriptLoaded(true);
              if (window.Beacon) {
                // We need to use the window.Beacon here, as safeBeacon won't have been updated with the isBeaconScriptLoaded var set to true.
                window.Beacon('config', beaconConfig);
                window.Beacon('init', helpscoutMetaData.beaconId);
                window.Beacon('on', 'open', addCloseButtonToBeacon);
              }
            })
            .catch((error: Error) => {
              if (error.name === 'DuplicateScriptError') {
                // We ignore the duplicate, but set loaded state to true in case we re-mounted this component.
                setIsBeaconScriptLoaded(true);
              } else {
                // Let any other errors bubble up
                throw error;
              }
            });
        }
      });
    }
    // Cleanup on unmount
    return () => window.removeEventListener('load', injectBeaconScript);
  }, [getHelpscoutMetadataQuery, isBeaconScriptLoaded, isLoggedIn]);

  return <BeaconContext.Provider value={{ beacon: safeBeacon }}>{children}</BeaconContext.Provider>;
};
